import { Case, CaseRatingSystem, Category, Group, Patient, Task, TaskDTO, TaskWorkgroup } from '@/models';
import {
  AuthService,
  CaseRatingService,
  CaseService,
  CaseUtilsService,
  CategoryService,
  CommonService,
  ErrorService,
  GroupService,
  ImageService,
  LanguageService,
  NotificationService,
  PatientService,
  StaticSiteService,
} from '@/services';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SpinnerButtonConfig } from '@/ui/spinner-button/spinner-button-config';
import { ChangePatientDialogComponent } from '../../components/change-patient-dialog/change-patient-dialog.component';
import { ConfirmChangingRatingSystemComponent } from '../../components/confirm-changing-rating-system/confirm-changing-rating-system.component';
import { CreateQuizInformationDialogComponent } from '../../components/create-quiz-information-dialog/create-quiz-information-dialog.component';
import { SelectStaticSiteDialogComponent } from '../../components/select-static-site-dialog/select-static-site-dialog.component';
import { IframeDialogComponent } from '../../components/iframe-dialog/iframe-dialog.component';

@Component({
  selector: 'app-edit-case',
  templateUrl: './edit-case.component.html',
  styleUrls: ['./edit-case.component.scss'],
  standalone: false,
})
export class EditCaseComponent implements OnInit {
  caseTitleFormControl: UntypedFormControl = new UntypedFormControl('', [Validators.required]);
  iframeUrlFormControl: UntypedFormControl = new UntypedFormControl('');
  aiChatUrlFormControl: UntypedFormControl = new UntypedFormControl('');

  /** 'custom' = typed iframeUrl, 'uploaded' = case.staticSite picked via the select-static-site dialog. */
  embedSourceFormControl: UntypedFormControl = new UntypedFormControl('custom');

  ratingFormControl: UntypedFormControl = new UntypedFormControl(null, [Validators.required]);
  roleFormControl: UntypedFormControl = new UntypedFormControl(null, [Validators.required]);

  case: Case;
  patientImageUrl: string;
  loading: boolean = false;
  changes: boolean = false;
  groupsLoading: boolean = false;
  patientLoading: boolean = false;
  new: boolean = false;
  tasksLoading: boolean = false;
  availableFunctions: Category[] = [];
  allGroups: Group[] = [];

  ratingSystems: CaseRatingSystem[] = [];
  ratingSystemFormControlTooltip: string = '';

  categories: Category[];

  isSuperAdmin: boolean;

  btnConfig: SpinnerButtonConfig = {
    text: `Opslaan`,
  };

  constructor(
    private router: Router,
    private caseService: CaseService,
    public dialog: MatDialog,
    private authService: AuthService,
    private groupService: GroupService,
    private categoryService: CategoryService,
    private caseRatingService: CaseRatingService,
    private notificationService: NotificationService,
    private errorService: ErrorService,
    private patientService: PatientService,
    private imageService: ImageService,
    private caseUtilsService: CaseUtilsService,
    private commonService: CommonService,
    private languageService: LanguageService,
    private staticSiteService: StaticSiteService
  ) {
    this.btnConfig.text = this.languageService.translateWithCap('general.actions.save');
  }

  ngOnInit(): void {
    this.isSuperAdmin = this.authService.isUserSuperAdmin();
    var id = this.router.url.split('/')[2];
    if (id == 'nieuw') {
      this.new = true;
      this.case = new Case();
      this.case.patient = new Patient();
      this.case.tasks = [];
      this.getCaseRatingSystems();
      return;
    }
    // get all groups.
    this.groupsLoading = true;
    this.groupService.getGroups(true).subscribe(resp => {
      this.groupsLoading = false;
      this.allGroups = resp;
    });

    this.loading = true;
    this.tasksLoading = true;
    this.caseService.getCaseById(id).subscribe(
      resp => {
        this.loading = false;
        this.case = resp as Case;
        if (this.case.lockedByAdmin && !this.isSuperAdmin) {
          history.back();
        }
        if (!this.case.connectedGroups) {
          this.case.connectedGroups;
        }
        void this.loadCasePatient();
        this.groupsLoading = true;
        this.caseService.getCaseTasksForCaseId(id, true).subscribe(
          resp => {
            this.case.tasks = (resp as TaskDTO[]).map(x => x.task);
            this.tasksLoading = false;
            this.case.tasks.sort((a, b) => a.order - b.order);
            this.getAvailableFunctionsForCaseTasks();
          },
          err => this.errorService.onError(err)
        );

        this.caseTitleFormControl.setValue(this.case.title);
        this.iframeUrlFormControl.setValue(this.case.iframeUrl);
        this.aiChatUrlFormControl.setValue(this.case.aiChatUrl);
        if (this.case.role) {
          this.roleFormControl.setValue(this.case.role.id);
        }
        if (this.case.caseRatingSystem) {
          this.ratingFormControl.setValue(this.case.caseRatingSystem.id);
        }
        if (this.case.staticSite) {
          this.embedSourceFormControl.setValue('uploaded');
        } else {
          this.embedSourceFormControl.setValue('custom');
        }

        this.getCaseRatingSystems();
      },
      err => this.errorService.onError(err)
    );
  }

  openEditWorkgroups() {
    this.saveCase().then(caseId => {
      this.router.navigateByUrl(`casussen/${caseId}/wijzigen`, { skipLocationChange: false }).then(() => {
        this.router.navigateByUrl(`casussen/${caseId}/werkgroepen`);
      });
    });
  }

  getAvailableFunctionsForCaseTasks() {
    this.categoryService.getCategoriesForInstitutionId(this.case.patient.department.careInstitution.id).subscribe(
      resp => {
        this.availableFunctions = [];
        var categories = resp as Category[];
        for (var cat of categories) {
          var catFuncs = [];
          for (var func of cat.categoryAppFunctions) {
            catFuncs.push(func);
            for (let task of this.case.tasks) {
              if (task.functionToSubmit && task.functionToSubmit.id) {
                if (func.appFunction.id == task.functionToSubmit.id) {
                  task.functionToSubmit = func.appFunction;
                }
              }
            }
          }
          if (catFuncs.length > 0) {
            cat.categoryAppFunctions = catFuncs;
            cat.categoryAppFunctions.sort((a, b) => a.order - b.order);
            this.availableFunctions.push(cat);
          }
        }
      },
      err => this.errorService.onError(err)
    );
  }

  onTitleChange() {
    if (this.caseTitleFormControl.value != this.case.title) {
      this.case.title = this.caseTitleFormControl.value;
      this.changes = true;
    }
  }

  getCaseRatingSystems() {
    this.caseRatingService.getCaseRatingSystems().subscribe(
      resp => {
        this.ratingSystems = resp as CaseRatingSystem[];
      },
      err => this.errorService.onError(err)
    );
  }

  onIframeUrlChange() {
    if (this.iframeUrlFormControl.value != this.case.iframeUrl) {
      this.case.iframeUrl = this.iframeUrlFormControl.value;
      this.changes = true;
    }
  }

  onEmbedSourceChange() {
    this.changes = true;
    if (this.embedSourceFormControl.value === 'custom') {
      this.case.staticSite = null;
    } else {
      this.case.iframeUrl = '';
      this.iframeUrlFormControl.setValue('');
    }
  }

  /** Opens a dialog listing type 'CASE' static sites (searchable/paginated — see SelectStaticSiteDialogComponent)
   * rather than an inline dropdown, since this list can get long. */
  openStaticSitePickerDialog() {
    const dialogRef = this.dialog.open(SelectStaticSiteDialogComponent, {
      minWidth: '600px',
      panelClass: 'card-primary',
    });
    dialogRef.afterClosed().subscribe(selected => {
      if (selected) {
        this.changes = true;
        this.case.staticSite = selected;
        this.case.iframeUrl = '';
        this.iframeUrlFormControl.setValue('');
      }
    });
  }

  openStaticSitePreview() {
    if (this.case.staticSite) {
      // Opened as an in-page dialog rather than window.open(cloudfrontUrl, '_blank') — a new tab has no
      // in-memory auth state, and the WAF on the static-sites CDN requires a depd.online Referer, which
      // a same-document dialog satisfies (like normal in-page embedding) and a new tab wouldn't.
      this.dialog.open(IframeDialogComponent, {
        width: '90vw',
        maxWidth: '90vw',
        height: '90vh',
        maxHeight: '90vh',
        panelClass: 'iframe-modal',
        data: this.staticSiteService.buildPublicUrl(this.case.staticSite.id),
      });
    }
  }

  onAiChatUrlChange() {
    if (this.aiChatUrlFormControl.value != this.case.aiChatUrl) {
      this.case.aiChatUrl = this.aiChatUrlFormControl.value;
      this.changes = true;
    }
  }

  onRoleChange() {
    if (!this.case.role || this.roleFormControl.value != this.case.role.id) {
      for (let role of this.case.patient.department.roles) {
        if (role.id == this.roleFormControl.value) {
          this.case.role = role;
          this.changes = true;
          return;
        }
      }
    }
  }

  onIsQuizChange() {
    if (this.case.isQuiz) {
      let i = this.case.tasks.findIndex(x => x.isQuiz == true);
      if (!i || i < 0) {
        let task = new Task();
        task.title = this.languageService.translateWithCap('case.quiz.task-title');
        task.isQuiz = true;
        task.id = this.makeRandom(this.lengthOfCode, this.possible);
        this.case.tasks.push(task);
      }
    }
  }

  onRatingSystemSelectChange(event: any) {
    const hasCaseRatingRegistration = this.case.tasks.some(task =>
      task.registrations.some(
        registration =>
          registration.registrations && registration.registrations.some(reg => reg.caseRatingRegistration != null)
      )
    );

    if (hasCaseRatingRegistration) {
      const dialogRef = this.dialog.open(ConfirmChangingRatingSystemComponent, {
        maxWidth: '400px',
      });
      dialogRef.afterClosed().subscribe(resp => {
        if (resp) {
          this.changes = true;
          var caseRating = this.ratingSystems.find(x => x.id == event.value);
          if (caseRating) {
            this.case.caseRatingSystem = caseRating;
            this.ratingSystemFormControlTooltip = '';
            this.case.caseRatingSystem.caseRatings.sort((a, b) => a.order - b.order);
            for (let rating of this.case.caseRatingSystem.caseRatings) {
              this.ratingSystemFormControlTooltip += `${rating.name}\n`;
            }
          }
        } else {
          this.ratingFormControl.setValue(this.case.caseRatingSystem.id);
        }
      });
    } else {
      this.changes = true;
      var caseRating = this.ratingSystems.find(x => x.id == event.value);
      if (caseRating) {
        this.case.caseRatingSystem = caseRating;
        this.ratingSystemFormControlTooltip = '';
        this.case.caseRatingSystem.caseRatings.sort((a, b) => a.order - b.order);
        for (let rating of this.case.caseRatingSystem.caseRatings) {
          this.ratingSystemFormControlTooltip += `${rating.name}\n`;
        }
      }
    }
  }

  getPatientProperty(property: string) {
    if (this.case.patient.properties) {
      return this.case.patient.properties.find(x => x.property.name == property)?.value;
    }
    return '';
  }

  private async loadCasePatient(): Promise<void> {
    const { data } = await this.patientService.getPatientById(this.case.patient.id);
    this.patientLoading = false;
    if (data) {
      this.case.patient.properties = (data as unknown as Patient).properties;
      this.patientImageUrl = this.case.patient.properties.find(x => x.property.name === 'foto')
        ? this.imageService.buildImageUrl(this.case.patient.properties.find(x => x.property.name === 'foto')!.value)
        : 'icon';
    }
  }

  getImgUrl(patient: Patient): string {
    if (patient && patient.properties) {
      var imgId = patient.properties.find(x => x.property.name == 'foto')?.value;
      if (imgId) {
        return this.imageService.buildImageUrl(imgId);
      }
    }
    return 'icon';
  }

  changPatient() {
    const dialogRef = this.dialog.open(ChangePatientDialogComponent, {
      minWidth: '70vw',
      panelClass: 'card-primary',
    });
    dialogRef.afterClosed().subscribe(patient => {
      if (patient) {
        this.patientImageUrl = this.getImgUrl(patient);
        if (this.case.patient && this.case.patient.department) {
          if (this.case.patient.department.careInstitution.id != patient.department.careInstitution.id) {
            this.case.patient = patient;
            this.getAvailableFunctionsForCaseTasks();
            for (let task of this.case.tasks) {
              task.functionToSubmit = null;
            }
          } else {
            this.case.patient = patient;
          }
        } else {
          this.case.patient = patient;
          this.getAvailableFunctionsForCaseTasks();
          for (let task of this.case.tasks) {
            task.functionToSubmit = null;
          }
        }
      }
    });
  }

  editTask(task: Task) {
    task.edit = true;
  }

  saveEditTask(task: Task) {
    this.changes = true;
    task.edit = false;
  }

  cancelEditTask(task: Task) {
    task.edit = false;
  }

  deleteTask(task: Task) {
    this.case.tasks.splice(
      this.case.tasks.findIndex(x => x.id == task.id),
      1
    );
    this.changes = true;
  }

  addTask() {
    let task = new Task();
    task.title = '';
    task.registrations = [];
    task.edit = true;
    task.isQuiz = false;
    task.id = this.makeRandom(this.lengthOfCode, this.possible);
    this.case.tasks.push(task);
  }

  openIframeInNewTab() {
    window.open(this.iframeUrlFormControl.value, '_blank');
  }

  openQuizInformationDialog() {
    this.dialog.open(CreateQuizInformationDialogComponent, {
      minWidth: '70vw',
      panelClass: 'preview-dialog',
    });
  }

  back() {
    // this._location.back();
    this.router.navigateByUrl('casussen');
  }

  save() {
    this.saveCase().then(caseId => {
      this.router.navigateByUrl(`/casussen/${caseId}`);
    });
  }

  saveCase(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.new) {
        this.btnConfig.loading = true;
        for (let task of this.case.tasks) {
          if (task.registrations.length === 0) {
            let newTask = this.commonService.cloneObject<Task>(task);
            newTask.registrations = [];
            const workgroupIds = this.case.tasks[0].registrations.map(
              x => this.commonService.cloneObject<TaskWorkgroup>(x).workgroup.id
            );
            for (let workgroupId of workgroupIds) {
              newTask = this.caseUtilsService.updateTaskRegistrations(this.allGroups, newTask, task, workgroupId);
            }
            task.registrations = newTask.registrations;
          }
        }
        this.updateTaskListOrder();
        this.caseService.editCase(this.case).subscribe(
          resp => {
            this.btnConfig.loading = false;
            resolve(this.case.id);
          },
          err => this.errorService.onError(err)
        );
      } else {
        this.btnConfig.loading = true;
        this.updateTaskListOrder();
        this.caseService.addCase(this.case).subscribe(
          resp => {
            var newCase = resp as Case;
            this.btnConfig.loading = false;
            this.new = false;
            resolve(newCase.id);
          },
          err => this.errorService.onError(err)
        );
      }
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.case.tasks, event.previousIndex, event.currentIndex);
    this.changes = true;
  }

  saveLockState() {
    this.caseService.setCaseAdminLocked(this.case.id, this.case.lockedByAdmin).subscribe({
      next: () => {
        if (this.case.lockedByAdmin) {
          this.notificationService.success(this.languageService.translateWithCap('case.lock.notification.locked'));
        } else {
          this.notificationService.success(this.languageService.translateWithCap('case.lock.notification.unlocked'));
        }
      },
      error: e => this.errorService.onError(e),
    });
  }

  private updateTaskListOrder() {
    for (var i = 0; i < this.case.tasks.length; i++) {
      this.case.tasks[i].order = i;
    }
  }

  private makeRandom(lengthOfCode: number, possible: string) {
    let text = '';
    for (let i = 0; i < lengthOfCode; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }
  possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890-';
  lengthOfCode = 40;
}
